import React, { useState, useEffect, useCallback } from 'react';
import './incidents.css'
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import axios from 'axios';
import { ClipLoader } from "react-spinners";
import { useTranslation } from 'react-i18next';
import { formatInTimeZone } from 'date-fns-tz';
import { Alertype, Incident, Incidents, Status } from '../../interfaces/interfaces';
import { getAlertype, getIncident, getStatus, patchIncident, postIncident } from '../../services/requests';
import Table, { ColumnConfig } from '../../components/table/table';

const IncidentsComponent: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [dataStatus, setDataStatus] = useState<Status[]>([]);
    const { t } = useTranslation();
    const [data, setData] = useState([{ ID: "", Type: "", Description: "", Status: "", Status2: 0, Date: "" }]);
    const [dataStatusIncidents, setDataStatusIncidents] = useState<Alertype[]>([]);
    const [loadingBtn, setLoadingBtn] = useState(false);

    /**
     * Formatea una fecha en formato ISO a una cadena legible en formato `YYYY-MM-DD HH:mm:ss`.
     * @param isoString - La cadena de fecha en formato ISO (por ejemplo: "2023-05-05T14:30:00Z").
     * @returns Una cadena con la fecha y hora formateadas.
     */
    const formatDate = (isoString: string): string => {
        const timeZone = 'Europe/Madrid';
        return formatInTimeZone(isoString, timeZone, 'yyyy-MM-dd HH:mm:ss');
    };

    /**
     * Obtiene la información de incidentes, estados y tipos de alerta desde la API.
     * Luego transforma y almacena estos datos en el estado del componente.
     * - Filtra los incidentes con `Nincidente !== 0`.
     * - Convierte los valores numéricos a nombres legibles.
     * - Formatea la fecha de creación usando `formatDate`.
     * - Actualiza los estados `data`, `filteredData`, `dataStatus`, y `dataStatusIncidents`.
     * En caso de error, muestra una notificación con el mensaje correspondiente.
     */
    const getInformation = useCallback(async () => {
        try {

            const responseIncidents = await getIncident();

            if (responseIncidents.status >= 200 && responseIncidents.status < 300) {
                toast.success(`${responseIncidents.data.message}`);
            }

            const responseStatus = await getStatus();

            const responseAlertype = await getAlertype();

            const filteredStatusIncidents = (responseStatus.data.data as Status[]).filter(item => item.Tipo === "Ic");
            setDataStatus(filteredStatusIncidents);

            const transformedData = (responseIncidents.data.data as Incidents[])
                .filter(incident => incident.Nincidente !== 0)
                .map(incident => {
                    const statusMatch = (responseStatus.data.data as Status[]).find(item => item.Nestado === incident.Status);
                    const alertypeMatch = (responseAlertype.data.data as Alertype[]).find(item => item.NtipoInc === incident.Tipo);

                    return {
                        ID: incident.Nincidente.toString(),
                        Type: alertypeMatch ? alertypeMatch.TtipoInc : "",
                        Description: incident.Descripcion || "",
                        Status: statusMatch ? statusMatch.Testado : "",
                        Status2: statusMatch ? statusMatch.Nestado : 0,
                        Date: formatDate(incident.Fcreacion.toString() || ""),
                    };
                });

            setData(transformedData);
            setFilteredData(transformedData);

            setDataStatusIncidents(responseAlertype.data.data as Alertype[]);

        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || error;
            toast.error(`${errorMessage}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Efecto que se ejecuta una vez al montar el componente.
     * Llama a `getInformation` para cargar datos desde la API.
     */
    useEffect(() => {
        getInformation();
    }, [getInformation]);

    const [isFullScreen, setIsFullScreen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState(data);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const totalPage = itemsPerPage === filteredData.length ? 1 : Math.ceil(filteredData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = itemsPerPage === filteredData.length ? filteredData.length : startIndex + itemsPerPage;
    const currentData = filteredData.slice(startIndex, endIndex);

    /**
     * Cambia a la página siguiente si no se está en la última.
     */
    const handleNextPage = () => {
        if (currentPage < totalPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    /**
     * Cambia a la página anterior si no se está en la primera.
     */
    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    /**
     * Exporta los datos actuales (`data`) a un archivo Excel (.xlsx).
     * Utiliza SheetJS (XLSX) para generar y descargar el archivo.
     */
    const downloadDataAsXLSX = () => {
        const sanitizedData = data.map(({ Status2, ...rest }) => rest);
        const worksheet = XLSX.utils.json_to_sheet(sanitizedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Incidents Data');
        XLSX.writeFile(workbook, 'Incidents Data.xlsx');
    };

    /**
     * Filtra los datos (`data`) según el término de búsqueda (`searchTerm`).
     * Actualiza `filteredData` y resetea la página actual.
     */
    const handleSearch = () => {
        const filtered = data.filter((item) => {
            return Object.values(item).some(value =>
                value.toString().toLowerCase().includes(searchTerm.toLowerCase())
            );
        });
        setFilteredData(filtered);

        if (filtered.length > 0) {
            setCurrentPage(1);
        } else {
            setCurrentPage(0);
        }
    };

    const [modalTypeNew, setModalTypeNew] = useState('');
    const [modalDescriptionNew, setModalDescriptionNew] = useState('');
    const [modalStatusNew, setModalStatusNew] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('');
    const [modalDescription, setModalDescription] = useState('');
    const [modalStatus, setModalStatus] = useState('');

    /**
     * Abre el modal al hacer clic en una tarjeta.
     */
    const handleCardClick = () => {
        setShowModal(true);
    };

    /**
     * Cierra todos los modales relacionados con la gestión de incidentes.
     * - Restablece el estado de visibilidad de los modales (`showModal`, `showModalEdit`).
     * - Limpia los valores del formulario (`ID`, `Type`, `Description`, `Status`).
     * - Reinicia los errores de validación y el estado de carga del botón.
     */
    const closeModal = () => {
        setShowModal(false);
        setShowModalEdit(false);
        setSelectedID('');
        setModalID('');
        setModalType('');
        setModalDescription('');
        setModalStatus('');
        setLoadingBtn(false);
        setErrorType("");
        setErrorDescription("");
        setErrorStatus("");
    };

    /**
     * Cierra el modal si se hace clic fuera del contenido del mismo (en el overlay).
     * @param e - Evento de clic del ratón.
     */
    const handleOverlayClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).classList.contains('modalOverlay')) {
            closeModal();
        }
    };

    /**
     * Extrae el mensaje de error de un campo que puede ser un array o una cadena.
     * - Si `field` es un array, devuelve el primer elemento.
     * - Si `field` es una cadena, la devuelve tal cual.
     * - En cualquier otro caso, devuelve una cadena vacía.
     *
     * @param field - Campo que contiene el mensaje de error.
     * @returns El mensaje de error como string.
     */
    const extractErrorMessage = (field: any) => {
        if (Array.isArray(field)) return field[0];
        if (typeof field === 'string') return field;
        return '';
    };

    /**
     * Crea un nuevo incidente usando los valores del formulario del modal.
     * Valida los campos requeridos y realiza una solicitud POST a la API.
     * Muestra mensajes de éxito o error con `toast`.
     * Al finalizar, actualiza los datos y limpia el formulario.
     */
    const handleCreateIncident = async () => {

        setLoadingBtn(true);

        if (!modalTypeNew || !modalDescriptionNew || !modalStatusNew) {
            setErrorType(!modalTypeNew ? "Por favor complete este campo" : "");
            setErrorDescription(!modalDescriptionNew ? "Por favor complete este campo" : "");
            setErrorStatus(!modalStatusNew ? "Por favor complete este campo" : "");
            toast.error(`${t('incidents.pleaseCompleteAllFields')}.`);
            setLoadingBtn(false);
            return;
        }

        try {
            const statusMatch = dataStatus.find(item => item.Testado === modalStatusNew);
            const alertypeMatch = dataStatusIncidents.find(item => item.TtipoInc === modalTypeNew);

            const body = {
                Tipo: alertypeMatch?.NtipoInc,
                Descripcion: modalDescriptionNew,
                Status: statusMatch?.Nestado
            };

            const response = await postIncident(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation()
                closeModal();
            } else {
                toast.error(`${t('incidents.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('incidents.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorType(extractErrorMessage(errorData.Tipo));
            setErrorDescription(extractErrorMessage(errorData.Descripcion));
            setErrorStatus(extractErrorMessage(errorData.Status));

            toast.error(`${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    const [isSelectOpenType, setIsSelectOpenType] = useState(false);
    const handleSelectFocusType = () => setIsSelectOpenType(true);
    const handleSelectBlurType = () => setIsSelectOpenType(false);

    const [isSelectOpenStatus, setIsSelectOpenStatus] = useState(false);
    const handleSelectFocusStatus = () => setIsSelectOpenStatus(true);
    const handleSelectBlurStatus = () => setIsSelectOpenStatus(false);

    const [sortConfig, setSortConfig] = useState<{
        key: keyof typeof data[0] | null;
        direction: "asc" | "desc" | null;
    }>({ key: "Date", direction: "desc" });

    /**
     * Efecto que ordena los datos (`data`) por fecha de forma descendente cuando se actualizan.
     */
    useEffect(() => {
        const sortedData = [...data].sort((a, b) => (a.Date < b.Date ? 1 : -1));
        setFilteredData(sortedData);
    }, [data]);

    /**
     * Ordena los datos (`data`) por la clave proporcionada.
     * Si se hace clic dos veces en la misma clave, alterna la dirección de ordenamiento (asc/desc).
     * @param key - Clave por la cual se debe ordenar la tabla.
     */
    const sortData = (key: keyof typeof data[0]) => {
        let direction: "asc" | "desc" = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        const sortedData = [...data].sort((a, b) => {
            if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
            if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
            return 0;
        });

        setFilteredData(sortedData);
    };

    const [showModalEdit, setShowModalEdit] = useState(false);
    const [modalID, setModalID] = useState('');
    const [selectedID, setSelectedID] = useState('');

    /**
     * Actualiza un incidente existente con los datos del formulario del modal.
     * Valida los campos requeridos antes de enviar la solicitud PATCH a la API.
     * Muestra mensajes de éxito o error con `toast`.
     * Al finalizar, actualiza los datos, cierra el modal y limpia el formulario.
     */
    const handleUpdateIncident = async () => {
        if (!selectedID) return;

        setLoadingBtn(true);

        if (!modalType || !modalDescription || !modalStatus) {
            setErrorType(!modalType ? "Por favor complete este campo" : "");
            setErrorDescription(!modalDescription ? "Por favor complete este campo" : "");
            setErrorStatus(!modalStatus ? "Por favor complete este campo" : "");
            toast.error(`${t('incidents.pleaseCompleteAllFields')}.`);
            setLoadingBtn(false);
            return;
        }

        try {
            const statusMatch = dataStatus.find(item => item.Testado === modalStatus);
            const alertypeMatch = dataStatusIncidents.find(item => item.TtipoInc === modalType);

            const body = {
                id: modalID,
                Tipo: alertypeMatch?.NtipoInc,
                Descripcion: modalDescription,
                Status: statusMatch?.Nestado
            };

            const response = await patchIncident(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('incidents.theIncidentWithIDCouldNotBeEdited')}: ${selectedID}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('incidents.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorType(extractErrorMessage(errorData.Tipo));
            setErrorDescription(extractErrorMessage(errorData.Descripcion));
            setErrorStatus(extractErrorMessage(errorData.Status));

            toast.error(`${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    const [errorType, setErrorType] = useState('');
    const [errorDescription, setErrorDescription] = useState('');
    const [errorStatus, setErrorStatus] = useState('');

    const columns: ColumnConfig[] = [
        { key: 'ID', label: 'ID', sortable: true },
        { key: 'Type', label: t('incidents.type'), sortable: true },
        { key: 'Description', label: t('incidents.description'), sortable: true },
        {
            key: 'Status',
            label: t('incidents.status'),
            sortable: true,
            render: (row) => (
                <span className={`status status-${row.Status2}`}>
                    {row.Status}
                </span>
            ),
        },
        { key: 'Date', label: t('incidents.date'), sortable: true },
    ];

    return (
        <div className='containerIncidents'>
            {loading ? (
                <div className="loading">
                    <ClipLoader color="#007bff" size={50} />
                    <p>{t('incidents.loadingInformation')}</p>
                </div>
            ) : (
                <>
                    <div className='title'>
                        <h4>{t('incidents.incidents')}</h4>
                        <div>
                            <button className="btn btn-dark" onClick={handleCardClick}>
                                <i className="fas fa-plus"></i> {t('incidents.newIncident')}
                            </button>
                        </div>
                    </div>
                    <div className={`separator ${isFullScreen ? 'expanded' : ''}`}>
                        <Table
                            t={t}
                            titleKey="incidents.incidentsList"
                            placeholder='incidents.searchIncidents'
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            handleSearch={handleSearch}
                            downloadDataAsXLSX={downloadDataAsXLSX}
                            isFullScreen={isFullScreen}
                            setIsFullScreen={setIsFullScreen}

                            nameClassTable="tableIncidents"
                            columns={columns}
                            currentData={currentData}
                            sortConfig={sortConfig}
                            onSort={sortData}
                            onRowEdit={(row) => {
                                setShowModalEdit(true);
                                setSelectedID(row.ID);
                                setModalID(row.ID);
                                const selectedItem = filteredData.find((data) => data.ID === row.ID);
                                setModalType(selectedItem?.Type || '');
                                setModalDescription(selectedItem?.Description || '');
                                setModalStatus(selectedItem?.Status || '');
                            }}

                            handlePreviousPage={handlePreviousPage}
                            currentPage={currentPage}
                            totalPage={totalPage}
                            itemsPerPage={itemsPerPage}
                            filteredData={filteredData}
                            setItemsPerPage={setItemsPerPage}
                            setCurrentPage={setCurrentPage}
                            handleNextPage={handleNextPage}
                            data={data}
                        />
                    </div>

                    {(showModal || showModalEdit) && (
                        <div className='modalOverlay' onClick={handleOverlayClick}>
                            <div className='modalContent'>
                                <div className='title'>
                                    <h6>
                                        {showModal ? t('incidents.newIncident') : showModalEdit ? t('incidents.editIncident') : ''}
                                    </h6>
                                    <span className="closeIcon" onClick={() => {
                                        closeModal();
                                    }}>&times;</span>
                                </div>
                                {showModalEdit &&
                                    <div className='formInput'>
                                        <div className="input-row">
                                            <h6>ID</h6>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={modalID}
                                                placeholder="Search by ID"
                                                disabled
                                            />
                                        </div>
                                    </div>
                                }
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('incidents.type')}</h6>
                                        <div className="select-wrapper">
                                            <select
                                                className={`form-control ${errorType ? 'is-invalid' : ''}`}
                                                value={showModal ? modalTypeNew : modalType}
                                                onChange={(e) => {
                                                    if (showModal) { setModalTypeNew(e.target.value); }
                                                    if (!showModal) { setModalType(e.target.value); }
                                                    setErrorType('');
                                                }}
                                                onFocus={handleSelectFocusType}
                                                onBlur={handleSelectBlurType}
                                            >
                                                <option value="">{`${t('incidents.selectA2')} ${t('incidents.type').toLowerCase()}`} ...</option>
                                                {dataStatusIncidents.map((status) => (
                                                    <option key={status.NtipoInc} value={status.TtipoInc}>
                                                        {status.TtipoInc}
                                                    </option>
                                                ))}
                                            </select>
                                            <span className="icon">
                                                {isSelectOpenType ? (
                                                    <i className="fas fa-chevron-up"></i>
                                                ) : (
                                                    <i className="fas fa-chevron-down"></i>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    {errorType && (
                                        <div className="invalid-feedback d-block">{errorType}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('incidents.description')}</h6>
                                        <textarea
                                            className={`form-control ${errorDescription ? 'is-invalid' : ''}`}
                                            value={showModal ? modalDescriptionNew : modalDescription}
                                            onChange={(e) => {
                                                if (showModal) { setModalDescriptionNew(e.target.value); }
                                                if (!showModal) { setModalDescription(e.target.value); }
                                                setErrorDescription('');
                                            }}
                                            rows={3}
                                            placeholder={`${t('incidents.enterThe')} ${t('incidents.description').toLowerCase()}`}
                                        />
                                    </div>
                                    {errorDescription && (
                                        <div className="invalid-feedback d-block">{errorDescription}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('incidents.status')}</h6>
                                        <div className="select-wrapper">
                                            <select
                                                className={`form-control ${errorStatus ? 'is-invalid' : ''}`}
                                                value={showModal ? modalStatusNew : modalStatus}
                                                onChange={(e) => {
                                                    if (showModal) { setModalStatusNew(e.target.value); }
                                                    if (!showModal) { setModalStatus(e.target.value); }
                                                    setErrorStatus('');
                                                }}
                                                onFocus={handleSelectFocusStatus}
                                                onBlur={handleSelectBlurStatus}
                                            >
                                                <option value="">{t('incidents.selectAStatus')}...</option>
                                                {dataStatus.map((status) => (
                                                    <option key={status.Nestado} value={status.Testado}>
                                                        {status.Testado}
                                                    </option>
                                                ))}
                                            </select>
                                            <span className="icon">
                                                {isSelectOpenStatus ? (
                                                    <i className="fas fa-chevron-up"></i>
                                                ) : (
                                                    <i className="fas fa-chevron-down"></i>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    {errorStatus && (
                                        <div className="invalid-feedback d-block">{errorStatus}</div>
                                    )}
                                </div>
                                <div className='formBtn'>
                                    <button className="btn btn-dark" onClick={() => {
                                        if (showModal) handleCreateIncident();
                                        if (showModalEdit) handleUpdateIncident();
                                    }}
                                        disabled={loadingBtn}>
                                        {loadingBtn ? (
                                            <div className="loading-btn">
                                                <ClipLoader color="#007bff" size={20} />
                                            </div>
                                        ) : (
                                            showModal ? t('incidents.createIncident') : showModalEdit ? t('incidents.updateIncident') : ''
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default IncidentsComponent;